import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  writeBatch,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { ensureInviteLookup } from './chatCryptoFirestore';
import { db } from './firebase';
import { parseSortedThreadInviteTokens } from './p2pThreadInvitePath';

const THREADS = 'p2pChatThreads';
const MESSAGES = 'messages';

export { p2pThreadId, p2pThreadIdFromSortedTokens, parseSortedThreadInviteTokens } from './p2pThreadInvitePath';

/** Сырые поля из Firestore (до расшифровки в хуке). */
export type P2PRawMessage = {
  id: string;
  fromInviteToken: string;
  senderUid: string;
  ts: number;
  /** Легаси: открытый текст */
  legacyText?: string;
  cipherTextB64?: string;
  ivB64?: string;
};

function msgRef(threadId: string) {
  return collection(db, THREADS, threadId, MESSAGES);
}

function queryDocToP2PRaw(d: QueryDocumentSnapshot): P2PRawMessage {
  const x = d.data() as {
    text?: string;
    cipherTextB64?: string;
    ivB64?: string;
    fromInviteToken?: string;
    senderUid?: string;
    createdAt?: { toMillis?: () => number };
  };
  const ts = x.createdAt?.toMillis?.() ?? Date.now();
  return {
    id: d.id,
    fromInviteToken: x.fromInviteToken ?? '',
    senderUid: x.senderUid ?? '',
    ts,
    legacyText: typeof x.text === 'string' ? x.text : undefined,
    cipherTextB64: typeof x.cipherTextB64 === 'string' ? x.cipherTextB64 : undefined,
    ivB64: typeof x.ivB64 === 'string' ? x.ivB64 : undefined,
  };
}

export type P2PMessagesTailMeta = {
  /** Документ с наименьшим createdAt среди пакета «хвоста» (для пагинации назад). */
  oldestInTail: QueryDocumentSnapshot | null;
  /** Если true, в треде могут быть более старые сообщения за пределами хвоста. */
  tailMayHaveOlder: boolean;
};

/**
 * Подписка на последние `tailLimit` сообщений (createdAt по убыванию, в колбэке — по возрастанию).
 */
export function subscribeP2PMessagesTail(
  threadId: string,
  tailLimit: number,
  onMessages: (list: P2PRawMessage[], meta: P2PMessagesTailMeta) => void,
  onError?: (e: Error) => void
): Unsubscribe {
  const q = query(msgRef(threadId), orderBy('createdAt', 'desc'), limit(tailLimit));

  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs;
      const list = docs.map((d) => queryDocToP2PRaw(d)).reverse();
      const oldestInTail = docs.length > 0 ? docs[docs.length - 1]! : null;
      const tailMayHaveOlder = docs.length >= tailLimit;
      onMessages(list, { oldestInTail, tailMayHaveOlder });
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  );
}

/** Одна страница сообщений старше курсора (по убыванию createdAt в запросе, результат по возрастанию). */
export async function fetchP2PMessagesOlderPage(
  threadId: string,
  cursor: QueryDocumentSnapshot,
  pageSize: number
): Promise<{ messages: P2PRawMessage[]; snapshots: QueryDocumentSnapshot[] }> {
  const q = query(
    msgRef(threadId),
    orderBy('createdAt', 'desc'),
    startAfter(cursor),
    limit(pageSize)
  );
  const snap = await getDocs(q);
  const docs = snap.docs;
  return {
    messages: docs.map((d) => queryDocToP2PRaw(d)).reverse(),
    snapshots: docs,
  };
}

/**
 * Создаёт/обновляет документ треда. Поле **`participantInviteTokens`** ([a,b] лексикографически, `threadId === a + '__' + b`)
 * нужно правилам Firestore: читать тред и сообщения может только uid из `inviteLookup` для одного из токенов.
 */
export async function ensureP2PThreadDoc(threadId: string): Promise<void> {
  const parsed = parseSortedThreadInviteTokens(threadId);
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (parsed) {
    payload.participantInviteTokens = parsed;
  }
  await setDoc(doc(db, THREADS, threadId), payload, { merge: true });
}

/**
 * Новое сообщение от пользователя = он снова ведёт тред: убираем его uid из deletedByUids
 * и легаси-поля «последнее удаление», если они относились к этому отправителю.
 * Иначе вторая сторона бессрочно видит «собеседник удалил чат» после того, как тот просто удалил и вернулся.
 */
export async function clearSelfDeletionMarkersOnOutgoingMessage(params: {
  threadId: string;
  senderUid: string;
  fromInviteToken: string;
}): Promise<void> {
  const ref = doc(db, THREADS, params.threadId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const d = snap.data() as {
    deletedByUids?: unknown;
    chatDeletedByInviteToken?: string;
    chatDeletedByUid?: string;
  };
  const rawUids = d.deletedByUids;
  const uids = Array.isArray(rawUids)
    ? rawUids.filter((x): x is string => typeof x === 'string')
    : [];
  const legacyOnlyMine =
    uids.length === 0 &&
    typeof d.chatDeletedByInviteToken === 'string' &&
    d.chatDeletedByInviteToken === params.fromInviteToken;

  if (legacyOnlyMine) {
    await updateDoc(ref, {
      deletedByUids: arrayRemove(params.senderUid),
      chatDeletedByInviteToken: deleteField(),
      chatDeletedByUid: deleteField(),
      chatDeletedAt: deleteField(),
    });
  } else {
    await updateDoc(ref, {
      deletedByUids: arrayRemove(params.senderUid),
    });
  }
}

/**
 * Подписка на последние сообщения треда; из снимка выбирается самое новое от uid ≠ currentUid.
 * Нужна для индикатора непрочитанного в списке контактов без полной ленты.
 * В колбэк передаётся сырое сообщение (в т.ч. шифротекст) — для превью в уведомлениях после расшифровки.
 */
export function subscribeP2PLatestPeerMessage(
  threadId: string,
  currentUid: string,
  onUpdate: (msg: P2PRawMessage | null) => void,
  onError?: (e: Error) => void
): Unsubscribe {
  const q = query(msgRef(threadId), orderBy('createdAt', 'desc'), limit(80));

  return onSnapshot(
    q,
    (snap) => {
      for (const d of snap.docs) {
        const raw = queryDocToP2PRaw(d);
        if (!raw.senderUid || raw.senderUid === currentUid) continue;
        onUpdate(raw);
        return;
      }
      onUpdate(null);
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  );
}

/**
 * @param opts.tailLimit — сколько последних сообщений слушать (по умолчанию 500).
 */
export function subscribeP2PMessages(
  threadId: string,
  onMessages: (list: P2PRawMessage[]) => void,
  onError?: (e: Error) => void,
  opts?: { tailLimit?: number }
): Unsubscribe {
  const lim = opts?.tailLimit ?? 500;
  return subscribeP2PMessagesTail(
    threadId,
    lim,
    (list) => onMessages(list),
    onError
  );
}

export async function sendP2PEncryptedMessage(params: {
  threadId: string;
  cipherTextB64: string;
  ivB64: string;
  fromInviteToken: string;
  senderUid: string;
}): Promise<string> {
  await ensureInviteLookup(params.senderUid, params.fromInviteToken);
  await ensureP2PThreadDoc(params.threadId);
  const ref = await addDoc(collection(db, THREADS, params.threadId, MESSAGES), {
    cipherTextB64: params.cipherTextB64,
    ivB64: params.ivB64,
    encV: 1,
    fromInviteToken: params.fromInviteToken,
    senderUid: params.senderUid,
    createdAt: serverTimestamp(),
  });
  try {
    await clearSelfDeletionMarkersOnOutgoingMessage({
      threadId: params.threadId,
      senderUid: params.senderUid,
      fromInviteToken: params.fromInviteToken,
    });
  } catch {
    /* сброс маркеров удаления — вторично; не блокируем доставку сообщения */
  }
  return ref.id;
}

export type P2PThreadMeta = {
  chatDeletedByInviteToken?: string;
  chatDeletedByUid?: string;
  chatDeletedAtMs?: number;
  /** Уникальные uid участников, нажавших «удалить чат» у себя. */
  deletedByUids?: string[];
  /**
   * Галочки «прочитано»: participantInviteTokens[0|1] дочитал до этого client-ts (мс) сообщений.
   * Пишется только соответствующим участником (см. firestore.rules).
   */
  readThroughTs0?: number;
  readThroughTs1?: number;
};

function parseThreadMeta(snap: DocumentSnapshot): P2PThreadMeta {
  if (!snap.exists()) return {};
  const d = snap.data() as {
    chatDeletedByInviteToken?: string;
    chatDeletedByUid?: string;
    chatDeletedAt?: { toMillis?: () => number };
    deletedByUids?: unknown;
    readThroughTs0?: unknown;
    readThroughTs1?: unknown;
  };
  const at = d.chatDeletedAt;
  const rawUids = d.deletedByUids;
  const deletedByUids = Array.isArray(rawUids)
    ? rawUids.filter((x): x is string => typeof x === 'string')
    : undefined;
  const r0 = d.readThroughTs0;
  const r1 = d.readThroughTs1;
  return {
    chatDeletedByInviteToken:
      typeof d.chatDeletedByInviteToken === 'string' ? d.chatDeletedByInviteToken : undefined,
    chatDeletedByUid: typeof d.chatDeletedByUid === 'string' ? d.chatDeletedByUid : undefined,
    chatDeletedAtMs: at?.toMillis?.(),
    deletedByUids,
    readThroughTs0: typeof r0 === 'number' && Number.isFinite(r0) ? r0 : undefined,
    readThroughTs1: typeof r1 === 'number' && Number.isFinite(r1) ? r1 : undefined,
  };
}

/** Публикация «дочитал до ts» для текущего пользователя (индекс по лексикографическому порядку токенов в threadId). */
export async function publishP2PThreadReadThrough(params: {
  threadId: string;
  myInviteToken: string;
  peerInviteToken: string;
  readThroughMessageTs: number;
}): Promise<void> {
  const { threadId, myInviteToken, peerInviteToken, readThroughMessageTs } = params;
  if (!Number.isFinite(readThroughMessageTs) || readThroughMessageTs < 0) return;
  const sorted = [myInviteToken, peerInviteToken].sort() as [string, string];
  const t0 = sorted[0]!;
  if (myInviteToken !== t0 && myInviteToken !== sorted[1]) return;
  const idx = myInviteToken === t0 ? 0 : 1;
  const field = idx === 0 ? 'readThroughTs0' : 'readThroughTs1';
  await updateDoc(doc(db, THREADS, threadId), {
    [field]: Math.round(readThroughMessageTs),
  });
}

/** Собеседник увидит это в подписке на документ треда. */
export async function signalUserDeletedChatThread(params: {
  threadId: string;
  deletedByInviteToken: string;
  deletedByUid: string;
}): Promise<void> {
  const parsed = parseSortedThreadInviteTokens(params.threadId);
  const base: Record<string, unknown> = {
    deletedByUids: arrayUnion(params.deletedByUid),
    chatDeletedByInviteToken: params.deletedByInviteToken,
    chatDeletedByUid: params.deletedByUid,
    chatDeletedAt: serverTimestamp(),
  };
  if (parsed) {
    base.participantInviteTokens = parsed;
  }
  await setDoc(doc(db, THREADS, params.threadId), base, { merge: true });
}

export async function deleteP2PChatMessage(threadId: string, messageId: string): Promise<void> {
  await deleteDoc(doc(db, THREADS, threadId, MESSAGES, messageId));
}

export async function updateP2PMessageCipher(
  threadId: string,
  messageId: string,
  cipherTextB64: string,
  ivB64: string
): Promise<void> {
  await updateDoc(doc(db, THREADS, threadId, MESSAGES, messageId), {
    cipherTextB64,
    ivB64,
  });
}

/**
 * Если оба участника пометили чат удалённым — удаляем все сообщения и документ треда.
 * Вызывать после signalUserDeletedChatThread с uid текущего пользователя.
 */
export async function tryPurgeP2PThreadIfBothDeleted(threadId: string, currentUid: string): Promise<void> {
  const snap = await getDoc(doc(db, THREADS, threadId));
  if (!snap.exists()) return;
  const uids = snap.data().deletedByUids;
  if (!Array.isArray(uids) || uids.length < 2) return;
  const set = new Set(uids.filter((x): x is string => typeof x === 'string'));
  if (set.size < 2) return;
  if (!set.has(currentUid)) return;

  const cref = collection(db, THREADS, threadId, MESSAGES);
  for (;;) {
    const batchSnap = await getDocs(query(cref, limit(500)));
    if (batchSnap.empty) break;
    const batch = writeBatch(db);
    batchSnap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  await deleteDoc(doc(db, THREADS, threadId));
}

export function subscribeP2PThreadMeta(
  threadId: string,
  onMeta: (meta: P2PThreadMeta) => void,
  onError?: (e: Error) => void
): Unsubscribe {
  return onSnapshot(
    doc(db, THREADS, threadId),
    (snap) => onMeta(parseThreadMeta(snap)),
    (err) => onError?.(err instanceof Error ? err : new Error(String(err)))
  );
}
